// src/App.js
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import Layout from './components/Layout';
import Login from './pages/Login';
import CashManagement from './pages/CashManagement';
import { checkSession, getUserRole } from './services/authService';

// Lazy load all page components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const DashboardModular = lazy(() => import('./pages/DashboardModular'));
const Inventory = lazy(() => import('./pages/Inventory'));
const POS = lazy(() => import('./pages/POS'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Scheduling = lazy(() => import('./pages/Scheduling'));
const Customers = lazy(() => import('./pages/Customers'));
const Transactions = lazy(() => import('./pages/Transactions'));
const AiInsights = lazy(() => import('./pages/AiInsights'));
const SupplyChain = lazy(() => import('./pages/SupplyChain'));
const InvoiceUpload = lazy(() => import('./pages/InvoiceUpload'));
const BOMBuilder = lazy(() => import('./pages/BOMBuilder'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const VarianceDashboard = lazy(() => import('./pages/VarianceDashboard'));
const BookingCalendar = lazy(() => import('./pages/BookingCalendar'));
const DynamicChart = lazy(() => import('./pages/DynamicChart'));
const EmployeeManagement = lazy(() => import('./pages/EmployeeManagement'));
const ClientManagement = lazy(() => import('./pages/ClientManagement'));
const TimeClock = lazy(() => import('./pages/TimeClock'));
const MenuDisplay = lazy(() => import('./components/MenuDisplay'));
const CustomerFacingDisplay = lazy(() => import('./components/CustomerFacingDisplay'));
const KitchenDisplay = lazy(() => import('./components/KitchenDisplay'));
const FloorPlan = lazy(() => import('./pages/FloorPlan'));

// Role-based protected route wrapper: strictly verifies active session key and role
const ProtectedRoute = ({ children, allowedRoles }) => {
    if (!checkSession()) {
        return <Navigate to="/login" replace />;
    }
    const role = getUserRole();
    if (allowedRoles && !allowedRoles.includes(role)) {
        return <Navigate to="/login" replace />;
    }
    return children;
};

function App() {
    const [isAuthenticated, setIsAuthenticated] = React.useState(() => checkSession());

    // Listen for storage changes and explicit auth invalidations (e.g. reload or session key refresh)
    React.useEffect(() => {
        const checkAuth = () => {
            setIsAuthenticated(checkSession());
        };
        window.addEventListener('storage', checkAuth);
        window.addEventListener('sims-auth-changed', checkAuth);
        return () => {
            window.removeEventListener('storage', checkAuth);
            window.removeEventListener('sims-auth-changed', checkAuth);
        };
    }, []);

    const handleLogin = (token) => {
        setIsAuthenticated(true);
    };

    return (
        <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading...</div>}>
            <Routes>
                <Route path="/login" element={<Login onLogin={handleLogin} />} />
                <Route path="/" element={isAuthenticated ? <Layout /> : <Navigate to="/login" replace />}>
                    <Route index element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER']}><DashboardModular /></ProtectedRoute>} />
                    <Route path="dashboard-classic" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER']}><Dashboard /></ProtectedRoute>} />
                    <Route path="inventory" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><Inventory /></ProtectedRoute>} />
                    <Route path="pos" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER']}><POS /></ProtectedRoute>} />
                    <Route path="analytics" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><Analytics /></ProtectedRoute>} />
                    <Route path="scheduling" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><Scheduling /></ProtectedRoute>} />
                    <Route path="customers" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><Customers /></ProtectedRoute>} />
                    <Route path="cash" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER']}><CashManagement /></ProtectedRoute>} />
                    <Route path="transactions" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><Transactions /></ProtectedRoute>} />
                    <Route path="ai-insights" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><AiInsights /></ProtectedRoute>} />
                    <Route path="supply-chain" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><SupplyChain /></ProtectedRoute>} />
                    <Route path="invoice-upload" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><InvoiceUpload /></ProtectedRoute>} />
                    <Route path="bom-builder" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><BOMBuilder /></ProtectedRoute>} />
                    <Route path="marketplace" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><Marketplace /></ProtectedRoute>} />
                    <Route path="variance-dashboard" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><VarianceDashboard /></ProtectedRoute>} />
                    <Route path="booking-calendar" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><BookingCalendar /></ProtectedRoute>} />
                    <Route path="dynamic-chart" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><DynamicChart /></ProtectedRoute>} />
                    <Route path="employees" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']}><EmployeeManagement /></ProtectedRoute>} />
                    <Route path="clients" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN']}><ClientManagement /></ProtectedRoute>} />
                    <Route path="floor-plan" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER']}><FloorPlan /></ProtectedRoute>} />
                    <Route path="timeclock" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER']}><TimeClock /></ProtectedRoute>} />
                    <Route path="kitchen-display" element={<ProtectedRoute allowedRoles={['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER']}><KitchenDisplay /></ProtectedRoute>} />
                </Route>
                <Route path="/menu-display" element={<MenuDisplay />} />
                <Route path="/customer-facing-display" element={<CustomerFacingDisplay />} />
                <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />} />
            </Routes>
        </Suspense>
    );
}

export default App;
